import {
  Button,
  Container,
  Form,
  Header,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components-themed";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { createRef, useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../app/hook";
import { usePostConsentInformationTransferMutation } from "../../features/api";
import { Messages, getMessage } from "../../features/error/messages";
import { addNotification } from "../../features/error/notifications-slice";
import { getExt } from "../../utils/common";
import { formatLabel } from "../../utils/format";
import { FileUpload } from "../UI/file-upload/FileUpload";
import { showModal } from "../UI/modal/modal-slice";
import { COMMON_LABELS, COMMON_CONFIGS } from "../default-constants";
import { CONFIGS, CONTENTS, LABELS } from "./constants";
import ErrorTable, { MessageItem, getMessageItemList } from "./ErrorTable";
import { downloadBase64File } from "../../utils/downLoad";

export interface FileItem {
  label: string;
  dismissLabel: string;
}

/**
 * 画面
 */
export const Main = (props: any) => {
  const dispatch = useAppDispatch();

  // 移管可否事前確認用変数
  const transferPreCheckInputRef = useRef<HTMLInputElement>(null);
  // ファイルアップロード変数
  const [uploadTransferPreCheckFile, setUploadTransferPreCheckFile] =
    useState<File[]>();
  // エラーメッセージ用変数
  const [transferPreCheckErrorText, setTransferPreCheckErrorText] =
    useState("");

  // 承諾移管実施用変数
  const consentTransferInputRef = useRef<HTMLInputElement>(null);
  // ファイルアップロード変数
  const [uploadConsentTransferFile, setUploadConsentTransferFile] =
    useState<File>();
  // エラーメッセージ用変数
  const [consentTransferErrorText, setConsentTransferErrorText] = useState("");

  // 承諾移管情報転送API設定
  const [uploadConsentInformationTransferFiles] =
    usePostConsentInformationTransferMutation();

  // スピナー表示処理用
  const [isSpinnerDisplay, setIsSpinnerDisplay] = useState(false);
  // ボタン非活性用
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  // エラーメッセージ表示処理用
  const [transferErrorMessage, setTransferErrorMessage] = useState<
    MessageItem[]
  >([]);

  // ref を作成しスクロールさせたい場所にある Element にセット
  const transferErrorRef = createRef<HTMLDivElement>();

  // 承諾移管実施確認用ファイル設定
  const onChangeTransferPreCheckFile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const fileList = e.target.files;

    // ファイルが設定されていない場合は処理終了
    if (!fileList) {
      return;
    }
    const files = Array.from(fileList);

    // 承諾移管実施確認用ファイルが設定されている場合、ファイル形式のチェック
    if (files !== undefined) {
      files.forEach((file) => {
        // アップロードされたファイルの拡張子が「.csv」以外の場合はアラートを出して終了する。
        if (CONFIGS.FILE_EXTENSION_CSV !== getExt(file.name.toLowerCase())) {
          const messageId = "KGRERR180" as keyof Messages;
          // アラート処理
          dispatch(
            addNotification({
              msgId: messageId,
              msgArgs: [],
            })
          );
        }
      });
    }
    // 送信するファイル情報を設定
    setUploadTransferPreCheckFile(files);

    // エラーテキストを設定
    setTransferPreCheckErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  // 承諾移管実施用ファイルが設定されている場合、ファイル形式のチェック
  const onChangeConsentTransferFile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;

    // ファイルが設定されていない場合は処理終了
    if (!files?.[0]) {
      return;
    }

    // アップロードされたファイルの拡張子が「.csv」以外の場合はアラートを出して終了する。
    if (CONFIGS.FILE_EXTENSION_CSV !== getExt(files[0].name.toLowerCase())) {
      const messageId = "KGRERR180" as keyof Messages;
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [],
        })
      );
      return;
    }
    // 送信するファイル情報を設定
    setUploadConsentTransferFile(files[0]);

    // エラーテキストを設定
    setConsentTransferErrorText(LABELS.ERROR_TEXT_LABEL);
  };

  // 移管可否事前確認用アップロードボタンイベント
  const transferPreCheckUploadOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    // ファイルアップロード処理
    transferPreCheckInputRef.current?.click();
  };

  // 承諾移管実施用アップロードボタンイベント
  const consentTransferOnClick = (e: CustomEvent<ClickDetail>) => {
    e.preventDefault();
    // ファイルアップロード処理
    consentTransferInputRef.current?.click();
  };

  // 申請ボタン押下処理
  const onClickButton = async () => {
    const fileData = new FormData();

    // 入力チェックでエラーになった場合は処理終了
    if (!inputCheck()) {
      return;
    }

    // 確認ダイアログ表示メッセージ作成
    let contentMessage = COMMON_LABELS.CONTENTS.APPLICATION_CONFIRM;

    // 移管可否事前確認用ファイルが選択されている場合
    if (uploadTransferPreCheckFile !== undefined) {
      // 移管可否事前確認用アップロードファイル設定処理
      let fileNameLabel = "";

      // ファイルごとに確認メッセージの作成
      uploadTransferPreCheckFile.forEach((file) => {
        fileData.append(CONFIGS.FILE, file);
        fileData.append(CONFIGS.UPLOAD_TYPE, CONFIGS.TRANSFER_PRE_CHECK_FILE);
        //確認ダイアログ表示メッセージ追記;
        fileNameLabel += CONTENTS.APPLICATION_TRANSFER_CONFIRMATION_FILE;
        fileNameLabel += file.name;
      });

      // 確認用メッセージの格納
      contentMessage += formatLabel(
        CONTENTS.APPLICATION_TRANSFER_CONFIRMATION,
        [fileNameLabel]
      );
    }
    // 承諾移管実施用アップロードファイルが選択されている場合
    if (uploadConsentTransferFile !== undefined) {
      // 承諾移管実施用アップロードファイル設定処理
      fileData.append(CONFIGS.FILE, uploadConsentTransferFile);
      fileData.append(CONFIGS.UPLOAD_TYPE, CONFIGS.CONSENT_TRANSFER_FILE);

      //確認ダイアログ表示メッセージ追記
      contentMessage += formatLabel(CONTENTS.APPLICATION_TRANSFER, [
        uploadConsentTransferFile.name,
      ]);
    }

    //確認ダイアログ表示
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: contentMessage,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        positiveFn: () => {
          merchantApplication(fileData);
        },
      })
    );

    /** 同じファイルを連続アップロードできるように処理 */
    if (transferPreCheckInputRef.current) {
      transferPreCheckInputRef.current.value = "";
    }
    if (consentTransferInputRef.current) {
      consentTransferInputRef.current.value = "";
    }
  };

  // 入力チェック
  const inputCheck = () => {
    let errorFlag = true;
    // 両方ファイル未設定であるかどうかの判定
    if (
      uploadTransferPreCheckFile === undefined &&
      uploadConsentTransferFile === undefined
    ) {
      // ファイル未選択時のエラー表示
      const messageId = "KGRERR054" as keyof Messages;
      // エラーテキストを設定
      setTransferPreCheckErrorText(getMessage(messageId).label);
      setConsentTransferErrorText(getMessage(messageId).label);
      errorFlag = false;
    }

    // 両方設定されているかどうかの判定
    if (
      uploadTransferPreCheckFile !== undefined &&
      uploadConsentTransferFile !== undefined
    ) {
      // エラーメッセージ設定
      const messageId = "KGRERR179" as keyof Messages;
      setTransferPreCheckErrorText(getMessage(messageId).label);
      setConsentTransferErrorText(getMessage(messageId).label);
      errorFlag = false;
    }
    return errorFlag;
  };

  // 確認ボタンOK押下時の処理
  const merchantApplication = async (fileData: FormData) => {
    // スピナー起動
    setIsSpinnerDisplay(true);
    // ボタン非活性
    setIsButtonDisabled(true);

    // エラーテキストを初期化
    setConsentTransferErrorText("");
    setTransferPreCheckErrorText("");

    // 承諾移管情報転送API呼び出し
    uploadConsentInformationTransferFiles(fileData)
      .unwrap()
      .then((res) => {
        if (undefined !== res.item && null !== res.item) {
          // ファイルダウンロード処理実行
          downloadBase64File(res.item);
        }
        // エラー内容を初期化
        setTransferErrorMessage([]);

        // アップロード後初期化
        setUploadTransferPreCheckFile(undefined);
        setUploadConsentTransferFile(undefined);

        // アップロードAPIでエラーが発生した場合
        if (COMMON_CONFIGS.API.STATUS_ERROR === res.status) {
          if (
            res.errorMessageList !== null &&
            res.errorMessageList.length !== 0
          ) {
            setTransferErrorMessage(getMessageItemList(res.errorMessageList));
          }
        }
      })
      .finally(() => {
        // スピナー停止
        setIsSpinnerDisplay(false);
        // ボタン活性
        setIsButtonDisabled(false);
      });
  };

  // このコールバックを呼び出して ref.current.scrollIntoView() を呼び出してスクロール
  const scrollToBottomOfList = useCallback(() => {
    // エラー内容にスクロール
    transferErrorRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferErrorRef]);

  useEffect(() => {
    // ページが描画されたらリストの末尾までスクロール
    scrollToBottomOfList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferErrorMessage]);

  return (
    <Container
      header={
        <Header variant="h2" description="">
          {LABELS.APPLICATION_FILE}
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="xxl">
        <SpaceBetween direction="vertical" size="xxl">
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="l">
                {isSpinnerDisplay ? <Spinner size="big" /> : <></>}
                <Button
                  iconAlign="right"
                  onClick={onClickButton}
                  variant="primary"
                  disabled={isButtonDisabled}
                >
                  {COMMON_LABELS.BTNS.APPLY}
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween direction="vertical" size="xxl">
              <FileUpload
                inputRef={transferPreCheckInputRef}
                label={LABELS.FOR_TRANSFER_CONFIRMATION}
                name={"transferPreCheckFile"}
                placeholder={LABELS.FILE_PLACEHOLDER}
                accept={CONFIGS.FILE_EXTENSION_CSV}
                multiple={true}
                fileName={
                  uploadTransferPreCheckFile
                    ? uploadTransferPreCheckFile
                        .map((file) => file.name)
                        .join(",")
                    : ""
                }
                setUploadFiles={setUploadTransferPreCheckFile}
                errorText={transferPreCheckErrorText}
                setErrorText={setTransferPreCheckErrorText}
                onChange={onChangeTransferPreCheckFile}
                onUpload={transferPreCheckUploadOnClick}
              />
              <SpaceBetween direction="vertical" size="xxl">
                <FileUpload
                  inputRef={consentTransferInputRef}
                  label={LABELS.FOR_TRANSFER}
                  name={"consentTransferFile"}
                  placeholder={LABELS.FILE_PLACEHOLDER}
                  accept={CONFIGS.FILE_EXTENSION_CSV}
                  fileName={
                    uploadConsentTransferFile
                      ? uploadConsentTransferFile.name
                      : ""
                  }
                  setUploadFile={setUploadConsentTransferFile}
                  errorText={consentTransferErrorText}
                  setErrorText={setConsentTransferErrorText}
                  onChange={onChangeConsentTransferFile}
                  onUpload={consentTransferOnClick}
                  description={LABELS.CAUTIONARY_NOTE}
                />
              </SpaceBetween>
            </SpaceBetween>
          </Form>
        </SpaceBetween>
        <SpaceBetween direction="vertical" size="xxs">
          {transferErrorMessage.length !== 0 ? (
            <>
              <ErrorTable
                data={transferErrorMessage}
                title={LABELS.ERROR_TITLE}
              />
              <div id="transferPreCheckError" ref={transferErrorRef} />
            </>
          ) : (
            <></>
          )}
        </SpaceBetween>
      </SpaceBetween>
    </Container>
  );
};